import { useState } from 'react';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { RecordActions } from '../../components/RecordActions';
import { RECORD_FIELDS } from '../../components/recordFields';
import { KeyValue, Pill, FormGrid, Field, TextInput, SelectInput } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import { dateLabel } from '../../lib/format';
import type { Warehouse } from '../../lib/types';

// 仓库类型：可创建值必须与数据库 CHECK 约束一致（migrations/001：
// stock / raw_material / work_in_progress / finished_goods / returns / scrap），
// 否则创建会触发约束错误。
const WAREHOUSE_TYPE_OPTIONS = ['stock', 'raw_material', 'work_in_progress', 'finished_goods', 'returns', 'scrap'];
// 展示名：额外保留 customer_supplied（客户来料），供后续独立迁移扩展 CHECK、或导入/历史行渲染使用。
// 客户送来的零件单独入库，不并入公司自有库存数量与采购成本；该类型需迁移放开后再进入下方可选项。
const WAREHOUSE_TYPE_LABELS: Record<string, string> = {
  stock: '通用库存',
  raw_material: '原材料',
  work_in_progress: '在制品',
  finished_goods: '成品',
  returns: '退货暂存',
  scrap: '报废',
  customer_supplied: '客户来料',
};
function warehouseTypeLabel(t?: string | null): string {
  if (!t) return '—';
  return WAREHOUSE_TYPE_LABELS[t] || t;
}

function WarehouseDetail({ row }: { row: Warehouse }) {
  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        <span className="id">{row.code}</span>
        <h3>{row.name_zh}</h3>
        {row.name_es ? <span className="muted">· {row.name_es}</span> : null}
        <Pill tone={row.active ? 'ok' : 'neutral'} label={row.active ? '启用' : '停用'} />
      </div>
      <div className="kv-grid">
        <KeyValue k="仓库编码">{row.code}</KeyValue>
        <KeyValue k="中文名称">{row.name_zh}</KeyValue>
        <KeyValue k="西语名称">{row.name_es || '—'}</KeyValue>
        <KeyValue k="仓库类型">{warehouseTypeLabel(row.warehouse_type)}</KeyValue>
        <KeyValue k="状态">{row.active ? '启用' : '停用'}</KeyValue>
        <KeyValue k="ERPNext 编号">{row.legacy_erpnext_id || '—'}</KeyValue>
        <KeyValue k="更新时间">{dateLabel(row.updated_at)}</KeyValue>
      </div>
    </div>
  );
}

function NewWarehouseForm({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }) {
  const [f, setF] = useState<Record<string, string>>({ warehouse_type: 'stock' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: string, v: string) => setF((d) => ({ ...d, [k]: v }));

  const submit = async () => {
    setErr(null);
    if (!f.code?.trim()) { setErr('仓库编码为必填'); return; }
    if (!f.name_zh?.trim()) { setErr('中文名称为必填'); return; }
    setBusy(true);
    try {
      await api.post('/api/warehouses', {
        code: f.code.trim(),
        name_zh: f.name_zh.trim(),
        name_es: f.name_es || null,
        warehouse_type: f.warehouse_type || 'stock',
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '创建失败');
    } finally { setBusy(false); }
  };

  return (
    <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
      <div className="panel-head"><div><h2>新建仓库</h2><div className="desc">写入核心服务 /api/warehouses · 库存流水、领料、完工入库、客户来料的仓位归属</div></div></div>
      <div className="panel-body">
        {err ? <div className="notice bad" style={{ marginBottom: 12 }}>{err}</div> : null}
        <FormGrid>
          <Field label="仓库编码" required><TextInput value={f.code ?? ''} onChange={(e) => set('code', e.target.value)} placeholder="如 WH-RAW" /></Field>
          <Field label="中文名称" required><TextInput value={f.name_zh ?? ''} onChange={(e) => set('name_zh', e.target.value)} /></Field>
          <Field label="西语名称"><TextInput value={f.name_es ?? ''} onChange={(e) => set('name_es', e.target.value)} /></Field>
          <Field label="仓库类型">
            <SelectInput value={f.warehouse_type} onChange={(e) => set('warehouse_type', e.target.value)}>
              {WAREHOUSE_TYPE_OPTIONS.map((v) => <option key={v} value={v}>{WAREHOUSE_TYPE_LABELS[v]}</option>)}
            </SelectInput>
          </Field>
        </FormGrid>
        <div className="detail-actions">
          <button className="btn primary" onClick={submit} disabled={busy}>{busy ? '提交中…' : '创建仓库'}</button>
          <button className="btn" onClick={onCancel} disabled={busy}>取消</button>
        </div>
      </div>
    </section>
  );
}

export function WarehousesPage() {
  const { user } = useAuth();
  const [reloadSignal, setReloadSignal] = useState(0);
  const [creating, setCreating] = useState(false);
  const canWrite = can(user, 'warehouses', 'create');

  const columns: Column<Warehouse>[] = [
    { header: '仓库编码', render: (w) => <span className="num strong">{w.code}</span> },
    { header: '中文名称', render: (w) => w.name_zh },
    { header: '西语名称', render: (w) => <span className="muted">{w.name_es || '—'}</span> },
    { header: '类型', render: (w) => warehouseTypeLabel(w.warehouse_type) },
    { header: '状态', render: (w) => <Pill tone={w.active ? 'ok' : 'neutral'} label={w.active ? '启用' : '停用'} /> },
  ];

  return (
    <>
      {creating ? (
        <NewWarehouseForm onCancel={() => setCreating(false)} onCreated={() => { setCreating(false); setReloadSignal((n) => n + 1); }} />
      ) : null}
      <ListPage<Warehouse>
        title="仓库"
        desc="仓库主数据 · /api/warehouses，供库存余额、领料出库、完工入库与客户来料单独归属引用"
        endpoint="/api/warehouses"
        columns={columns}
        rowId={(w) => w.id}
        showYear={false}
        searchPlaceholder="搜索仓库编码 / 中文名 / 西语名…"
        reloadSignal={reloadSignal}
        rowActions={(row) => <RecordActions type="warehouses" row={row} fields={RECORD_FIELDS['warehouses']} label={String(row.code)} onDone={() => setReloadSignal((n) => n + 1)} />}
        actions={canWrite ? (
          <button className="btn primary" onClick={() => setCreating((v) => !v)}>{creating ? '收起新建' : '新建仓库'}</button>
        ) : undefined}
        renderDetail={(row) => <WarehouseDetail row={row} />}
      />
    </>
  );
}
